/**
 * Utility functions for the Group Progress Page.
 *
 * Reads schedule progress data from localStorage (via the data adapter)
 * and provides helpers for percentage extraction, color mapping,
 * aggregation, and filtering.
 */

import {
  getScheduleInstancesFromStorage,
  getInstanceSeriesFromStorage,
} from "../../kpi-engine/scheduleDataAdapter";
import {
  getFinanceInstancesFromStorage,
  getFinanceInstanceSeriesFromStorage,
} from "../../kpi-engine/budgetDataAdapter";
import {
  indicatorBucket,
  bucketToColor,
  type IndicatorBucket,
} from "../../utils/indicatorBuckets";
import { getProjectKpiConfigOverride } from "../../Data/ProjectConfig/projectKpiConfigOverrides";
import { getProjectOverride } from "../../Data/ProjectConfig/projectConfigOverrides";
import { communityConfigStore } from "../../Data/store";
import { getCommunityOverride } from "../../Data/CommunityConfig/communityConfigOverrides";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ProgressItem = {
  id: string;           // project_guid or group id
  label: string;        // project_name or group label
  percentage: number;   // 0–100 (actual)
  baselinePercentage: number | null; // 0–100, null when no baseline data
  warningPct: number | null;   // 0–100, baseline - yellowPP (null when no baseline)
  criticalPct: number | null;  // 0–100, baseline - redPP (null when no baseline)
  colorCode: number;    // 0=grey, 1=red, 2=yellow, 3=green
  cssColor: string;     // hex color derived from colorCode
  bucket: IndicatorBucket;
  isSubgroup: boolean;
  /** Hero/tile image URL for spotlight cards. null = use placeholder. */
  imageUrl: string | null;
  /** Original payload (project row or subgroup __payload) for click handling. */
  payload: any;
};

export type AggregationMethod = "average" | "worst" | "best";

// ---------------------------------------------------------------------------
// Schedule data helpers
// ---------------------------------------------------------------------------

/**
 * Check whether a project has any schedule progress series data.
 */
export function hasScheduleData(projectGuid: string): boolean {
  const pg = String(projectGuid || "").trim();
  if (!pg) return false;
  const instances = getScheduleInstancesFromStorage(pg);
  if (!instances.length) return false;
  for (const inst of instances) {
    const ig = String((inst as any)?.instance_guid || "").trim();
    const series = getInstanceSeriesFromStorage(pg, ig || undefined);
    if (series.length > 0) return true;
  }
  return false;
}

/**
 * Get the latest actual % complete (0–100) for a project's schedule.
 * Returns null if there is no schedule data.
 *
 * Mirrors the logic in ScheduleThermometer:
 *  1. Read all instances for the project.
 *  2. For each instance, get the series.
 *  3. Filter to actual rows (reporting_date ≤ today, actual_complete finite).
 *  4. Take the latest actual_complete.
 *  5. Normalise (scale factor) and clamp to 0–100.
 */
export function getProjectActualPercent(projectGuid: string): number | null {
  const pg = String(projectGuid || "").trim();
  if (!pg) return null;

  const instances = getScheduleInstancesFromStorage(pg);
  if (!instances.length) return null;

  const todayISO = new Date().toISOString().slice(0, 10);
  let bestPct: number | null = null;

  for (const inst of instances) {
    const ig = String((inst as any)?.instance_guid || "").trim();
    const series = getInstanceSeriesFromStorage(pg, ig || undefined);
    if (!series.length) continue;

    // Infer scale factor (same heuristic as ScheduleThermometer)
    const scaleFactor = inferScaleFactor(series);

    // Filter to actual rows
    const actualRows = series.filter(
      (r) =>
        r.reporting_date <= todayISO &&
        r.actual_complete !== null &&
        r.actual_complete !== undefined &&
        Number.isFinite(Number(r.actual_complete)),
    );

    if (!actualRows.length) {
      // Fall back to latest row in full series if no actual rows
      const last = series[series.length - 1];
      if (
        last &&
        last.actual_complete !== null &&
        last.actual_complete !== undefined &&
        Number.isFinite(Number(last.actual_complete))
      ) {
        const raw = Number(last.actual_complete);
        const pct = scaleFactor === 100 ? raw : raw * 100;
        const clamped = Math.max(0, Math.min(100, pct));
        if (bestPct === null || clamped > bestPct) bestPct = clamped;
      }
      continue;
    }

    const latest = actualRows[actualRows.length - 1];
    const raw = Number(latest.actual_complete);
    const pct = scaleFactor === 100 ? raw : raw * 100;
    const clamped = Math.max(0, Math.min(100, pct));
    if (bestPct === null || clamped > bestPct) bestPct = clamped;
  }

  return bestPct;
}

/**
 * Get the latest baseline % complete (0–100) for a project's schedule.
 * Returns null when there is no baseline data.
 *
 * Same approach as getProjectActualPercent but reads baseline_complete.
 */
export function getProjectBaselinePercent(projectGuid: string): number | null {
  const pg = String(projectGuid || "").trim();
  if (!pg) return null;

  const instances = getScheduleInstancesFromStorage(pg);
  if (!instances.length) return null;

  const todayISO = new Date().toISOString().slice(0, 10);
  let bestPct: number | null = null;

  for (const inst of instances) {
    const ig = String((inst as any)?.instance_guid || "").trim();
    const series = getInstanceSeriesFromStorage(pg, ig || undefined);
    if (!series.length) continue;

    const scaleFactor = inferScaleFactor(series);

    // Filter to baseline rows up to today
    const baselineRows = series.filter(
      (r) =>
        r.reporting_date <= todayISO &&
        r.baseline_complete !== null &&
        r.baseline_complete !== undefined &&
        Number.isFinite(Number(r.baseline_complete)),
    );

    if (!baselineRows.length) continue;

    const latest = baselineRows[baselineRows.length - 1];
    const raw = Number(latest.baseline_complete);
    const pct = scaleFactor === 100 ? raw : raw * 100;
    const clamped = Math.max(0, Math.min(100, pct));
    if (bestPct === null || clamped > bestPct) bestPct = clamped;
  }

  return bestPct;
}

/**
 * Read the variance tolerance PP thresholds for a project's schedule.
 * Returns { yellowPP, redPP } with defaults of 10 / 20.
 */
export function getProjectTolerancePP(projectGuid: string): {
  yellowPP: number;
  redPP: number;
} {
  const pg = String(projectGuid || "").trim();
  const defaults = { yellowPP: 10, redPP: 20 };
  if (!pg) return defaults;

  try {
    const ov = getProjectKpiConfigOverride(pg as any) as any;
    const cats = ov?.category_instances || {};
    const schedArr: any[] = cats?.schedule || [];

    // Use the first schedule instance that has tolerance values
    for (const inst of schedArr) {
      const yp = Number(inst?.variance_yellow_pp);
      const rp = Number(inst?.variance_red_pp);
      if (Number.isFinite(yp) && yp > 0) defaults.yellowPP = yp;
      if (Number.isFinite(rp) && rp > 0) defaults.redPP = rp;
      if (Number.isFinite(yp) || Number.isFinite(rp)) break;
    }

    // Fall back to global schedule thresholds
    const glbYellow = Number(ov?.schedule?.yellow_threshold_pp);
    const glbRed = Number(ov?.schedule?.red_threshold_pp);
    if (defaults.yellowPP === 10 && Number.isFinite(glbYellow) && glbYellow > 0)
      defaults.yellowPP = glbYellow;
    if (defaults.redPP === 20 && Number.isFinite(glbRed) && glbRed > 0)
      defaults.redPP = glbRed;
  } catch {
    // ignore — use defaults
  }

  return defaults;
}

/**
 * Compute warning and critical threshold positions (0–100) for a project.
 * Schedule progress is "increasing" direction, so:
 *   warning  = baseline - yellowPP
 *   critical = baseline - redPP
 * Returns null values when baseline is unavailable.
 */
function computeTolerancePositions(
  baselinePct: number | null,
  projectGuid: string,
): { warningPct: number | null; criticalPct: number | null } {
  if (baselinePct === null) return { warningPct: null, criticalPct: null };
  const { yellowPP, redPP } = getProjectTolerancePP(projectGuid);
  return {
    warningPct: Math.max(0, Math.min(100, baselinePct - yellowPP)),
    criticalPct: Math.max(0, Math.min(100, baselinePct - redPP)),
  };
}

/**
 * Resolve a hero/tile image URL for a project, checking config overrides
 * and raw project fields.  Returns null when no image is configured.
 */
function resolveProjectImageUrl(projectGuid: string, projectRow: any): string | null {
  // 1. Check project config override (where hero photos are stored)
  try {
    const pov = getProjectOverride(projectGuid);
    if (pov?.tile_image_url) return pov.tile_image_url;
    if (pov?.logo_url) return pov.logo_url;
  } catch { /* ignore */ }

  // 1b. Fallback: check KPI config override
  try {
    const ov = getProjectKpiConfigOverride(projectGuid as any) as any;
    if (ov?.tile_image_url) return ov.tile_image_url;
    if (ov?.logo_url) return ov.logo_url;
  } catch { /* ignore */ }

  // 2. Check raw project fields
  const p = projectRow?.project || projectRow;
  if (p?.hero_image_url) return p.hero_image_url;
  if (p?.tile_image_url) return p.tile_image_url;
  if (p?.logo_url) return p.logo_url;

  return null;
}

/**
 * Resolve a hero image URL for a geography subgroup by matching its label
 * to a community entity name.  Checks community config overrides first
 * (user uploads), then falls back to the entity's raw hero_image_url.
 * Returns null when no matching community or no hero image exists.
 */
function resolveSubgroupHeroImage(label: string): string | null {
  if (!label) return null;

  // Strip the " (N)" project-count suffix added by buildSubgroupItems
  const cleanLabel = label.replace(/\s*\(\d+\)\s*$/, "").trim();
  if (!cleanLabel) return null;

  const labelLower = cleanLabel.toLowerCase();

  // Scan communityConfigStore for an entity whose name matches
  for (const [guid, entity] of Array.from(communityConfigStore.entries())) {
    const entityName = String(entity?.name || "").trim();
    if (!entityName) continue;
    if (entityName.toLowerCase() !== labelLower) continue;

    // 1. Check community config override (user-uploaded hero)
    try {
      const ov = getCommunityOverride(guid) as any;
      if (ov?.hero_image_url) return ov.hero_image_url;
    } catch { /* ignore */ }

    // 2. Fall back to raw entity hero_image_url from API
    if (entity?.hero_image_url) return entity.hero_image_url;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Color helpers
// ---------------------------------------------------------------------------

/**
 * Map a numeric indicator color code to a CSS hex color.
 */
export function colorCodeToCSS(code: number): string {
  return bucketToColor(indicatorBucket(code));
}

/**
 * Map a numeric indicator color code to a bucket name.
 */
export function colorCodeToBucket(code: number): IndicatorBucket {
  return indicatorBucket(code);
}

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------

/**
 * Aggregate an array of percentage values using the specified method.
 */
export function aggregatePercents(
  values: number[],
  method: AggregationMethod,
): number {
  if (!values.length) return 0;
  switch (method) {
    case "worst":
      return Math.min(...values);
    case "best":
      return Math.max(...values);
    default: {
      const sum = values.reduce((a, b) => a + b, 0);
      return sum / values.length;
    }
  }
}

/**
 * Aggregate color codes using the same approach as KpiGridTable.
 */
export function aggregateColorCodes(
  codes: number[],
  method: AggregationMethod,
): number {
  const valid = codes.filter((c) => c > 0); // skip no-data (0)
  if (!valid.length) return 0;
  switch (method) {
    case "worst":
      return Math.min(...valid);
    case "best":
      return Math.max(...valid);
    default: {
      const avg = valid.reduce((a, b) => a + b, 0) / valid.length;
      return avg >= 2.5 ? 3 : avg >= 1.5 ? 2 : avg >= 0.5 ? 1 : 0;
    }
  }
}

// ---------------------------------------------------------------------------
// Build ProgressItem arrays
// ---------------------------------------------------------------------------

/**
 * Build ProgressItem[] for leaf projects in a group.
 * Only includes projects with schedule data.
 */
export function buildProjectItems(groupItems: any[]): ProgressItem[] {
  const items: ProgressItem[] = [];

  for (const p of groupItems || []) {
    const guid = String(
      p?.project_guid || p?.project?.project_guid || "",
    ).trim();
    if (!guid) continue;
    if (!hasScheduleData(guid)) continue;

    const pct = getProjectActualPercent(guid);
    if (pct === null) continue;

    const baselinePct = getProjectBaselinePercent(guid);
    const { warningPct, criticalPct } = computeTolerancePositions(baselinePct, guid);
    const colorCode = Number(p?.schedule_indicator_color ?? 0);
    const bucket = colorCodeToBucket(colorCode);

    items.push({
      id: guid,
      label: p?.project?.project_name || p?.project_name || "(unnamed)",
      percentage: pct,
      baselinePercentage: baselinePct,
      warningPct,
      criticalPct,
      colorCode,
      cssColor: colorCodeToCSS(colorCode),
      bucket,
      isSubgroup: false,
      imageUrl: resolveProjectImageUrl(guid, p),
      payload: p,
    });
  }

  // Sort alphabetically by label (matches KPIGridTable order)
  items.sort((a, b) => a.label.localeCompare(b.label));
  return items;
}

/**
 * Build ProgressItem[] for subgroups in a group-of-groups.
 * Each bar represents the aggregated progress of a subgroup.
 */
export function buildSubgroupItems(
  subRows: any[],
  method: AggregationMethod,
): ProgressItem[] {
  const items: ProgressItem[] = [];

  for (const sub of subRows || []) {
    if (sub?.__type !== "group") continue;

    const payload = sub.__payload || sub;
    const leafItems = payload?.group_items || payload?.__items || [];

    // Collect percentages from leaf projects that have schedule data
    const pcts: number[] = [];
    const baselinePcts: number[] = [];
    const warningPcts: number[] = [];
    const criticalPcts: number[] = [];
    const colorCodes: number[] = [];

    for (const p of leafItems) {
      const guid = String(
        p?.project_guid || p?.project?.project_guid || "",
      ).trim();
      if (!guid || !hasScheduleData(guid)) continue;
      const pct = getProjectActualPercent(guid);
      if (pct === null) continue;
      pcts.push(pct);
      const bpct = getProjectBaselinePercent(guid);
      if (bpct !== null) {
        baselinePcts.push(bpct);
        const { warningPct, criticalPct } = computeTolerancePositions(bpct, guid);
        if (warningPct !== null) warningPcts.push(warningPct);
        if (criticalPct !== null) criticalPcts.push(criticalPct);
      }
      const cc = Number(p?.schedule_indicator_color ?? 0);
      if (cc > 0) colorCodes.push(cc);
    }

    // Skip subgroups with no schedule data at all
    if (!pcts.length) continue;

    const aggPct = aggregatePercents(pcts, method);
    const aggBaseline = baselinePcts.length
      ? aggregatePercents(baselinePcts, method)
      : null;
    const aggWarning = warningPcts.length
      ? aggregatePercents(warningPcts, method)
      : null;
    const aggCritical = criticalPcts.length
      ? aggregatePercents(criticalPcts, method)
      : null;
    const aggColor = aggregateColorCodes(colorCodes, method);
    const bucket = colorCodeToBucket(aggColor);

    const subLabel = payload?.groupLabel || "(unnamed)";
    items.push({
      id: payload?.id || payload?.groupLabel || "",
      label: `${subLabel} (${pcts.length})`,
      percentage: aggPct,
      baselinePercentage: aggBaseline,
      warningPct: aggWarning,
      criticalPct: aggCritical,
      colorCode: aggColor,
      cssColor: colorCodeToCSS(aggColor),
      bucket,
      isSubgroup: true,
      imageUrl: resolveSubgroupHeroImage(subLabel),
      payload,
    });
  }

  // Sort alphabetically by label
  items.sort((a, b) => a.label.localeCompare(b.label));
  return items;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Infer whether series values are on a 0–1 or 0–100 scale.
 * Mirrors ScheduleThermometer logic.
 */
function inferScaleFactor(
  rows: Array<{ actual_complete: number | null; baseline_complete: number | null; forecast_complete: number | null }>,
): number {
  let maxV = 0;
  for (const r of rows) {
    for (const v of [r.actual_complete, r.baseline_complete, r.forecast_complete]) {
      const n = Number(v);
      if (Number.isFinite(n) && n > maxV) maxV = n;
    }
  }
  return maxV > 1.5 ? 100 : 1;
}

// ---------------------------------------------------------------------------
// Finance data helpers
// ---------------------------------------------------------------------------

/**
 * Check whether a project has any finance cost series data.
 */
export function hasFinanceData(projectGuid: string): boolean {
  const pg = String(projectGuid || "").trim();
  if (!pg) return false;
  const instances = getFinanceInstancesFromStorage(pg);
  if (!instances.length) {
    // Fall back: check project-level data (no instance config required)
    const series = getFinanceInstanceSeriesFromStorage(pg, undefined);
    return series.length > 0;
  }
  for (const inst of instances) {
    const ig = String((inst as any)?.instance_guid || "").trim();
    const series = getFinanceInstanceSeriesFromStorage(pg, ig || undefined);
    if (series.length > 0) return true;
  }
  return false;
}

/**
 * Get the latest actual spend as a percentage of the scale factor (0–100+).
 * For finance, percentage = (actual_spend / scaleFactor) * 100.
 * Unlike schedule, this CAN exceed 100% (over-budget).
 */
export function getProjectFinanceActualPercent(projectGuid: string): number | null {
  const pg = String(projectGuid || "").trim();
  if (!pg) return null;

  const instances = getFinanceInstancesFromStorage(pg);
  const todayISO = new Date().toISOString().slice(0, 10);

  // Helper to extract actual % from a series
  const extractActualPct = (series: any[]): number | null => {
    if (!series.length) return null;
    const scaleFactor = inferFinanceScaleFactor(series);
    const actualRows = series.filter(
      (r: any) =>
        r.reporting_date <= todayISO &&
        r.actual_spend !== null &&
        r.actual_spend !== undefined &&
        Number.isFinite(Number(r.actual_spend)),
    );
    if (!actualRows.length) return null;
    const latest = actualRows[actualRows.length - 1];
    const raw = Number(latest.actual_spend);
    return scaleFactor > 0 ? Math.max(0, (raw / scaleFactor) * 100) : 0;
  };

  if (!instances.length) {
    return extractActualPct(getFinanceInstanceSeriesFromStorage(pg, undefined));
  }

  let bestPct: number | null = null;
  for (const inst of instances) {
    const ig = String((inst as any)?.instance_guid || "").trim();
    const series = getFinanceInstanceSeriesFromStorage(pg, ig || undefined);
    const pct = extractActualPct(series);
    if (pct !== null && (bestPct === null || pct > bestPct)) bestPct = pct;
  }
  return bestPct;
}

/**
 * Get the latest baseline budget as a percentage of the scale factor (0–100).
 */
export function getProjectFinanceBaselinePercent(projectGuid: string): number | null {
  const pg = String(projectGuid || "").trim();
  if (!pg) return null;

  const instances = getFinanceInstancesFromStorage(pg);
  const todayISO = new Date().toISOString().slice(0, 10);

  const extractBaselinePct = (series: any[]): number | null => {
    if (!series.length) return null;
    const scaleFactor = inferFinanceScaleFactor(series);
    const baselineRows = series.filter(
      (r: any) =>
        r.reporting_date <= todayISO &&
        r.baseline_budget !== null &&
        r.baseline_budget !== undefined &&
        Number.isFinite(Number(r.baseline_budget)),
    );
    if (!baselineRows.length) return null;
    const latest = baselineRows[baselineRows.length - 1];
    const raw = Number(latest.baseline_budget);
    return scaleFactor > 0 ? Math.max(0, (raw / scaleFactor) * 100) : 0;
  };

  if (!instances.length) {
    return extractBaselinePct(getFinanceInstanceSeriesFromStorage(pg, undefined));
  }

  let bestPct: number | null = null;
  for (const inst of instances) {
    const ig = String((inst as any)?.instance_guid || "").trim();
    const series = getFinanceInstanceSeriesFromStorage(pg, ig || undefined);
    const pct = extractBaselinePct(series);
    if (pct !== null && (bestPct === null || pct > bestPct)) bestPct = pct;
  }
  return bestPct;
}

/**
 * Read finance variance tolerance PP thresholds.
 * For finance, thresholds are ABOVE baseline (over-budget = bad).
 */
export function getProjectFinanceTolerancePP(projectGuid: string): {
  yellowPP: number;
  redPP: number;
} {
  const pg = String(projectGuid || "").trim();
  const defaults = { yellowPP: 10, redPP: 20 };
  if (!pg) return defaults;
  try {
    const ov = getProjectKpiConfigOverride(pg as any) as any;
    const cats = ov?.category_instances || {};
    const finArr: any[] = cats?.finance || [];
    for (const inst of finArr) {
      const yp = Number(inst?.variance_yellow_pp);
      const rp = Number(inst?.variance_red_pp);
      if (Number.isFinite(yp) && yp > 0) defaults.yellowPP = yp;
      if (Number.isFinite(rp) && rp > 0) defaults.redPP = rp;
      if (Number.isFinite(yp) || Number.isFinite(rp)) break;
    }
    const glbYellow = Number(ov?.finance?.yellow_threshold_pp);
    const glbRed = Number(ov?.finance?.red_threshold_pp);
    if (defaults.yellowPP === 10 && Number.isFinite(glbYellow) && glbYellow > 0)
      defaults.yellowPP = glbYellow;
    if (defaults.redPP === 20 && Number.isFinite(glbRed) && glbRed > 0)
      defaults.redPP = glbRed;
  } catch { /* use defaults */ }
  return defaults;
}

/**
 * Finance tolerance positions — thresholds are ABOVE baseline (over-budget = bad).
 */
function computeFinanceTolerancePositions(
  baselinePct: number | null,
  projectGuid: string,
): { warningPct: number | null; criticalPct: number | null } {
  if (baselinePct === null) return { warningPct: null, criticalPct: null };
  const { yellowPP, redPP } = getProjectFinanceTolerancePP(projectGuid);
  return {
    warningPct: Math.max(0, baselinePct + yellowPP),
    criticalPct: Math.max(0, baselinePct + redPP),
  };
}

/**
 * Infer finance scale factor from series data.
 */
function inferFinanceScaleFactor(
  rows: Array<{ actual_spend?: number | null; baseline_budget?: number | null; forecast_spend?: number | null }>,
): number {
  let maxV = 0;
  for (const r of rows) {
    for (const v of [r.actual_spend, r.baseline_budget, r.forecast_spend]) {
      const n = Number(v);
      if (Number.isFinite(n) && n > maxV) maxV = n;
    }
  }
  return maxV > 0 ? maxV : 1;
}

// ---------------------------------------------------------------------------
// Finance build functions
// ---------------------------------------------------------------------------

/**
 * Build ProgressItem[] for leaf projects using finance data.
 * Only includes projects with finance data.
 */
export function buildFinanceProjectItems(groupItems: any[]): ProgressItem[] {
  const items: ProgressItem[] = [];

  for (const p of groupItems || []) {
    const guid = String(
      p?.project_guid || p?.project?.project_guid || "",
    ).trim();
    if (!guid) continue;
    if (!hasFinanceData(guid)) continue;

    const pct = getProjectFinanceActualPercent(guid);
    if (pct === null) continue;

    const baselinePct = getProjectFinanceBaselinePercent(guid);
    const { warningPct, criticalPct } = computeFinanceTolerancePositions(baselinePct, guid);
    const colorCode = Number(p?.finance_indicator_color ?? 0);
    const bucket = colorCodeToBucket(colorCode);

    items.push({
      id: guid,
      label: p?.project?.project_name || p?.project_name || "(unnamed)",
      percentage: pct,
      baselinePercentage: baselinePct,
      warningPct,
      criticalPct,
      colorCode,
      cssColor: colorCodeToCSS(colorCode),
      bucket,
      isSubgroup: false,
      imageUrl: resolveProjectImageUrl(guid, p),
      payload: p,
    });
  }

  items.sort((a, b) => a.label.localeCompare(b.label));
  return items;
}

/**
 * Build ProgressItem[] for subgroups using finance data.
 */
export function buildFinanceSubgroupItems(
  subRows: any[],
  method: AggregationMethod,
): ProgressItem[] {
  const items: ProgressItem[] = [];

  for (const sub of subRows || []) {
    if (sub?.__type !== "group") continue;

    const payload = sub.__payload || sub;
    const leafItems = payload?.group_items || payload?.__items || [];

    const pcts: number[] = [];
    const baselinePcts: number[] = [];
    const warningPcts: number[] = [];
    const criticalPcts: number[] = [];
    const colorCodes: number[] = [];

    for (const p of leafItems) {
      const guid = String(
        p?.project_guid || p?.project?.project_guid || "",
      ).trim();
      if (!guid || !hasFinanceData(guid)) continue;
      const pct = getProjectFinanceActualPercent(guid);
      if (pct === null) continue;
      pcts.push(pct);
      const bpct = getProjectFinanceBaselinePercent(guid);
      if (bpct !== null) {
        baselinePcts.push(bpct);
        const { warningPct, criticalPct } = computeFinanceTolerancePositions(bpct, guid);
        if (warningPct !== null) warningPcts.push(warningPct);
        if (criticalPct !== null) criticalPcts.push(criticalPct);
      }
      const cc = Number(p?.finance_indicator_color ?? 0);
      if (cc > 0) colorCodes.push(cc);
    }

    if (!pcts.length) continue;

    const aggPct = aggregatePercents(pcts, method);
    const aggBaseline = baselinePcts.length
      ? aggregatePercents(baselinePcts, method)
      : null;
    const aggWarning = warningPcts.length
      ? aggregatePercents(warningPcts, method)
      : null;
    const aggCritical = criticalPcts.length
      ? aggregatePercents(criticalPcts, method)
      : null;
    const aggColor = aggregateColorCodes(colorCodes, method);
    const bucket = colorCodeToBucket(aggColor);

    const finSubLabel = payload?.groupLabel || "(unnamed)";
    items.push({
      id: payload?.id || payload?.groupLabel || "",
      label: `${finSubLabel} (${pcts.length})`,
      percentage: aggPct,
      baselinePercentage: aggBaseline,
      warningPct: aggWarning,
      criticalPct: aggCritical,
      colorCode: aggColor,
      cssColor: colorCodeToCSS(aggColor),
      bucket,
      isSubgroup: true,
      imageUrl: resolveSubgroupHeroImage(finSubLabel),
      payload,
    });
  }

  items.sort((a, b) => a.label.localeCompare(b.label));
  return items;
}

// ---------------------------------------------------------------------------
// Score-based build functions (Summary, Mission, Data)
// ---------------------------------------------------------------------------

/**
 * Build ProgressItem[] for leaf projects using a score field (0–100).
 * Used for Summary, Mission, and Data categories which don't have
 * time-series progress data but do have indicator scores on each project row.
 *
 * @param scoreKey — e.g. "mission_score_0_100"
 * @param colorKey — e.g. "mission_indicator_color"
 */
export function buildScoreProjectItems(
  groupItems: any[],
  scoreKey: string,
  colorKey: string,
): ProgressItem[] {
  const items: ProgressItem[] = [];
  // DEV-851: Detect Data Reliability category by colorKey identity. Used to
  // apply the blue Complete-state override for closed projects below.
  const isReliability = colorKey === "data_reliability_color";

  for (const p of groupItems || []) {
    const guid = String(
      p?.project_guid || p?.project?.project_guid || "",
    ).trim();
    if (!guid) continue;

    const isCompleted = isReliability && !!(p as any)?._isCompletedProject;

    const rawScore = Number(p?.[scoreKey] ?? NaN);
    // DEV-851: Skip non-finite scores UNLESS this is a completed reliability
    // bar — we still want the project visible in the chart with the blue
    // Complete treatment, even though Now's score is null.
    if (!Number.isFinite(rawScore) && !isCompleted) continue;

    const pct = Number.isFinite(rawScore) ? Math.max(0, Math.min(100, rawScore)) : 100;
    const colorCode = Number(p?.[colorKey] ?? 0);
    // DEV-851: Override bucket + cssColor to blue Complete for closed
    // projects on the reliability category. The colorCode field is left
    // at its raw value (typically 0) so debug surfaces show the engine's
    // actual output; only the visible bucket / color is overridden.
    const bucket = isCompleted ? "completed" : colorCodeToBucket(colorCode);
    const cssColor = isCompleted ? bucketToColor("completed") : colorCodeToCSS(colorCode);

    items.push({
      id: guid,
      label: p?.project?.project_name || p?.project_name || "(unnamed)",
      percentage: pct,
      baselinePercentage: null, // no baseline for score-based categories
      warningPct: null,
      criticalPct: null,
      colorCode,
      cssColor,
      bucket,
      isSubgroup: false,
      imageUrl: resolveProjectImageUrl(guid, p),
      payload: p,
    });
  }

  items.sort((a, b) => a.label.localeCompare(b.label));
  return items;
}

/**
 * Build ProgressItem[] for subgroups using a score field.
 */
export function buildScoreSubgroupItems(
  subRows: any[],
  method: AggregationMethod,
  scoreKey: string,
  colorKey: string,
): ProgressItem[] {
  const items: ProgressItem[] = [];

  for (const sub of subRows || []) {
    if (sub?.__type !== "group") continue;

    const payload = sub.__payload || sub;
    const leafItems = payload?.group_items || payload?.__items || [];

    const pcts: number[] = [];
    const colorCodes: number[] = [];

    for (const p of leafItems) {
      const rawScore = Number(p?.[scoreKey] ?? NaN);
      if (!Number.isFinite(rawScore)) continue;
      pcts.push(Math.max(0, Math.min(100, rawScore)));
      const cc = Number(p?.[colorKey] ?? 0);
      if (cc > 0) colorCodes.push(cc);
    }

    if (!pcts.length) continue;

    const aggPct = aggregatePercents(pcts, method);
    const aggColor = aggregateColorCodes(colorCodes, method);
    const bucket = colorCodeToBucket(aggColor);

    const scoreSubLabel = payload?.groupLabel || "(unnamed)";
    items.push({
      id: payload?.id || payload?.groupLabel || "",
      label: `${scoreSubLabel} (${pcts.length})`,
      percentage: aggPct,
      baselinePercentage: null,
      warningPct: null,
      criticalPct: null,
      colorCode: aggColor,
      cssColor: colorCodeToCSS(aggColor),
      bucket,
      isSubgroup: true,
      imageUrl: resolveSubgroupHeroImage(scoreSubLabel),
      payload,
    });
  }

  items.sort((a, b) => a.label.localeCompare(b.label));
  return items;
}
